// LayoutEditorSharedToolbar.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './LayoutEditorSharedToolbar.css';

// Components
import PageEditorToolbarSection from '../toolbarsection/PageEditorToolbarSection';
import Divider from 'components/divider/Divider';

/**
 * LayoutEditorSharedToolbar
 *
 * Renders the shared floating toolbar for the active layout container and block.
 *
 * @param {ReactNode[]} blockActionNodes - Icon actions for the active block section.
 * @param {string} containerLabel - Label for the active container section.
 * @param {ReactNode[]} containerActionNodes - Icon actions for the active container section.
 * @param {ReactNode[]} contentActionNodes - Optional content actions for the active block section.
 * @param {boolean} preserveEditorFocus - Whether toolbar pointer interactions should avoid stealing focus.
 * @param {string} placement - Vertical placement relative to the anchor: above or below.
 * @param {object} positionStyle - Absolute positioning styles for the toolbar.
 *
 * @returns {ReactNode} The rendered shared toolbar.
 */
const LayoutEditorSharedToolbar = React.forwardRef(({
	blockActionNodes = [],
	containerActionNodes = [],
	containerLabel = '',
	contentActionNodes = [],
	placement = 'below',
	preserveEditorFocus = false,
	positionStyle = {},
}, ref) => {

	const { theme } = useTheme();

	const resolvedBlockActionNodes = Array.isArray(blockActionNodes) ? blockActionNodes.filter(Boolean) : [];
	const resolvedContentActionNodes = Array.isArray(contentActionNodes) ? contentActionNodes.filter(Boolean) : [];
	const showBlockSection = resolvedBlockActionNodes.length > 0;
	const showContentSection = resolvedContentActionNodes.length > 0;

	/**
 * Handles mouse down capture.
 *
 * @param {*} event
 */
	const handleMouseDownCapture = (event) => {
		if (!preserveEditorFocus) return;
		event.preventDefault();
	};
	return (
		<div
			className={[
				'layout-editor-shared-toolbar-container',
				placement === 'above' ? 'layout-editor-shared-toolbar-container-above' : '',
			].filter(Boolean).join(' ')}
			style={{
				//backgroundColor: theme.backgroundMuted,
				...positionStyle,
			}}
			ref={ref}
			onMouseDownCapture={handleMouseDownCapture}
		>
			<PageEditorToolbarSection
				actionNodes={containerActionNodes}
				label={containerLabel}
			/>

			{showBlockSection || showContentSection ? (
				<>
					<Divider
						color={theme.backgroundMuted}
						direction="vertical"
						height={showContentSection ? 94 : 47}
						margin="6px"
					/>

					{/* LAYOUT EDITOR SHARED TOOLBAR SECTIONS */}
					<div className="layout-editor-shared-toolbar-sections">
						{showBlockSection ? (
							<PageEditorToolbarSection
								actionNodes={resolvedBlockActionNodes}
								label="BLOCK"
							/>
						) : null}

						{showContentSection ? (
							<PageEditorToolbarSection
								actionNodes={resolvedContentActionNodes}
								label="CONTENT"
							/>
						) : null}
					</div>
				</>
			) : null}
		</div>
	);
});

// Dev Tools
LayoutEditorSharedToolbar.displayName = 'LayoutEditorSharedToolbar';

export default LayoutEditorSharedToolbar;
